import { Alert, Button, Input, Modal, Select } from 'antd'
import React, { FC, useEffect } from 'react'
import { ModalProps } from 'antd/lib/modal'
import { useTranslation } from 'react-i18next'
import { filter } from 'lodash'
import { CloseOutlined } from '@ant-design/icons'

export type InflationClauseDocumentOption = {
	slot: 1 | 2
	label: string
}

type Props = ModalProps & {
	visible: boolean,
	initEmail: string[],
	// inflacna dolozka - prilohy vybraneho bytoveho druzstva (aktivne iba ak je zapnuta inflacna dolozka)
	isInflationClause?: boolean,
	documents?: InflationClauseDocumentOption[],
	onCancel: Function | React.MouseEvent<HTMLElement, MouseEvent>,
	handleOk: (emails: string[], inflationClauseDocumentSlot: 1 | 2 | null) => void,
}

const EmailPickerModal: FC<Props> = (props) => {
	const { t } = useTranslation('pages')

	const {
		visible,
		initEmail,
		isInflationClause = false,
		documents = [],
		onCancel,
		handleOk
	} = props

	const [emailList, setEmailList] = React.useState<string[]>([])
	const [email, setEmail] = React.useState<string>('')
	const [selectedSlot, setSelectedSlot] = React.useState<1 | 2 | null>(null)

	// prilohy sa zobrazuju iba ak je zapnuta inflacna dolozka a druzstvo ma platne prilohy
	const activeDocuments = isInflationClause ? documents : []
	const requiresSelection = activeDocuments.length > 1
	const singleDocument = activeDocuments.length === 1 ? activeDocuments[0] : null

	useEffect(() => {
		if (visible) {
			// pri jednej prilohe ju predvyberieme automaticky, inak vynulujeme
			setSelectedSlot(activeDocuments.length === 1 ? activeDocuments[0].slot : null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, isInflationClause, documents.length])

	const resolveSlotToSend = (): 1 | 2 | null => {
		if (activeDocuments.length === 0) {
			return null
		}
		if (singleDocument) {
			return singleDocument.slot
		}
		return selectedSlot
	}

	const isSubmitDisabled = requiresSelection && selectedSlot == null

	return (
		<Modal
			visible={visible}
			title={t('pages:Odosli cenovú ponuku na emaily')}
			onCancel={onCancel}
			footer={[
				<Button key='back' onClick={onCancel}>
					{t('pages:Zrušiť')}
				</Button>,
				<Button
					key='submit'
					type='primary'
					disabled={isSubmitDisabled}
					onClick={() => {
						handleOk(emailList, resolveSlotToSend())
					}}
				>
					{t('pages:Odoslať')}
				</Button>
			]}
		>
			{initEmail.concat(emailList)?.map((email, index) => (
				<div className={'mb-4'} key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
					<div key={index}>
						{email}
					</div>
					{initEmail.includes(email) ? null : <CloseOutlined onClick={() => setEmailList(filter(emailList, (e) => e !== email))}/>}
				</div>
			))}
			<div
				style={{ display: 'flex', justifyContent: 'space-between' }}>
				<Input
					className={'mr-5'}
					placeholder="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
				/>
				<Button
					onClick={() => {
						setEmailList([...emailList, email])
						setEmail('')
					}}
				>
					{t('pages:Pridať email')}
				</Button>
			</div>

			{singleDocument && (
				<Alert
					className={'mt-4'}
					type={'info'}
					showIcon
					message={`${t('pages:K ponuke bude priložená inflačná doložka')}: ${singleDocument.label}`}
				/>
			)}

			{requiresSelection && (
				<div className={'mt-4'}>
					<div className={'font-semibold mb-2'}>
						{t('pages:Vyberte inflačnú doložku, ktorá sa odošle zákazníkovi')}
					</div>
					<Select
						style={{ width: '100%' }}
						size={'large'}
						placeholder={t('pages:Vyberte prílohu')}
						value={selectedSlot ?? undefined}
						onChange={(value) => setSelectedSlot(value as 1 | 2)}
						options={activeDocuments.map((doc) => ({ value: doc.slot, label: doc.label }))}
					/>
				</div>
			)}

		</Modal>
	)
}

export default EmailPickerModal
